import { fetchByIds, fetchOrgMembersWith } from "@/lib/large-org";
/**
 * Reports Hub — top-level entry showing 4 category cards + a role-scoped Overview.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { listDarSheets as listDarSheetsFn } from "@/lib/dar.functions";
import { useEffect, useState } from "react";
import {
  ClipboardList,
  AlertTriangle,
  RefreshCcw,
  ShieldAlert,
  ChevronRight,
  Filter,
  Loader2,
  FileText,
  ExternalLink,
  LayoutTemplate,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


export const Route = createFileRoute("/_authenticated/reports/")({
  component: ReportsHub,
  head: () => ({
    meta: [
      { title: "Reports — MissionReady" },
      {
        name: "description",
        content:
          "Mission Ready reports hub: shift tasks, incident reports, follow-ups, and critical/special escalations.",
      },
    ],
  }),
});

type Category = {
  to: "/reports/tasks" | "/reports/incident" | "/reports/follow-up" | "/reports/critical";
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconBg: string;
  iconFg: string;
  badge?: { label: string; tone: "danger" };
};

const CATEGORIES: Category[] = [
  { to: "/reports/tasks", title: "Tasks", subtitle: "Daily shift reports — start, hourly observation, close-out.", icon: ClipboardList, iconBg: "bg-[#C0C0C0]/12", iconFg: "text-[#C0C0C0]" },
  { to: "/reports/incident", title: "Incident Reporting", subtitle: "Document an incident — alarms, disturbances, threats and more.", icon: AlertTriangle, iconBg: "bg-primary/15", iconFg: "text-primary" },
  { to: "/reports/follow-up", title: "Follow Up", subtitle: "View and update past reports, add notes, escalate or close.", icon: RefreshCcw, iconBg: "bg-[#C0C0C0]/12", iconFg: "text-[#C0C0C0]" },
  { to: "/reports/critical", title: "Critical & Special Reports", subtitle: "Escalations, vehicle accidents, weapons, equipment, maintenance.", icon: ShieldAlert, iconBg: "bg-primary/20", iconFg: "text-primary", badge: { label: "Priority", tone: "danger" } },
];

const OVERVIEW_ROLES = ["org_owner", "admin", "manager", "supervisor", "dispatcher"];

type SiteOpt = { id: string; name: string };
type OfficerOpt = { id: string; name: string };
type Row = {
  kind: "incident" | "shift";
  id: string;
  title: string;
  subtitle: string;
  site_id: string | null;
  officer_id: string | null;
  at: string;
  link: string;
  severity?: string;
};

function ReportsHub() {
  const { currentOrg, currentRole } = useAuth();
  const canSeeOverview = OVERVIEW_ROLES.includes(currentRole ?? "");

  return (
    <div className="px-4 pb-28 pt-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-3xl">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Operations Center</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-foreground md:text-4xl">Reports</h1>
        <p className="mt-2 text-sm text-muted-foreground">Choose a category to file or review a report.</p>

        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <li key={c.to}>
                <Link
                  to={c.to}
                  className="group block h-full rounded-2xl border border-[#C0C0C0]/15 bg-card p-5 shadow-card transition-all hover:border-primary/50 hover:bg-card/80 active:scale-[0.99]"
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${c.iconBg} ${c.iconFg} ring-1 ring-inset ring-[#C0C0C0]/15`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="font-display text-lg font-semibold text-foreground">{c.title}</h2>
                        {c.badge && (
                          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">{c.badge.label}</span>
                        )}
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{c.subtitle}</p>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>

        <CustomTemplatesSection />

        {currentOrg && <DarSheetsSection orgId={currentOrg.id} canSeeAll={canSeeOverview} />}

        {canSeeOverview && currentOrg && <ReportsOverview orgId={currentOrg.id} />}
      </div>
    </div>
  );
}

/** Finalized D.A.R. Shift Sheets — the digital record stays canonical and playable. */
function DarSheetsSection({ orgId, canSeeAll }: { orgId: string; canSeeAll: boolean }) {
  const listSheets = useServerFn(listDarSheetsFn);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listSheets({ data: { orgId, mine: !canSeeAll, limit: 10 } })
      .then((r: any) => { if (alive) setRows(r ?? []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgId, canSeeAll, listSheets]);

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold">D.A.R. Shift Sheets</h2>
        <Button asChild variant="ghost" size="sm">
          <Link to="/dar">Open current sheet</Link>
        </Button>
      </div>
      {loading ? (
        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No submitted shift sheets yet.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to="/dar/$reportId"
                params={{ reportId: r.id }}
                className="flex items-center gap-3 rounded-xl border border-[#C0C0C0]/15 bg-card p-3 text-sm transition-colors hover:border-primary/50"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">
                    {r.shift_date ?? "—"} · {r.guard_name}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {r.entry_count ?? 0} occurrence{(r.entry_count ?? 0) === 1 ? "" : "s"} · submitted{" "}
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : ""}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function CustomTemplatesSection() {
  // Custom report templates are available on every plan, so this section always
  // renders live, tappable controls instead of a locked, inert preview.
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="font-display text-lg font-semibold tracking-tight text-foreground">
          Custom Report Templates
        </h2>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Build your own report forms with a drag-and-drop field builder. Included on every plan.
      </p>

      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <LayoutTemplate className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-foreground">Template builder</div>
            <div className="text-[11px] text-muted-foreground">
              Create and manage custom report forms for your team.
            </div>
          </div>
          <Button asChild size="sm" className="gap-1">
            <Link to="/reports/templates">
              Open builder <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>

  );
}

function ReportsOverview({ orgId }: { orgId: string }) {
  const [sites, setSites] = useState<SiteOpt[]>([]);
  const [officers, setOfficers] = useState<OfficerOpt[]>([]);
  const [siteId, setSiteId] = useState<string>("");
  const [officerId, setOfficerId] = useState<string>("");
  const [type, setType] = useState<"all" | "incident" | "shift">("all");
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState<string>(sevenDaysAgo);
  const [to, setTo] = useState<string>(today);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: m }] = await Promise.all([
        supabase.from("sites").select("id,name").eq("organization_id", orgId).order("name"),
        fetchOrgMembersWith(supabase, orgId).then((d) => ({ data: d })),
      ]);
      setSites((s ?? []) as SiteOpt[]);
      const ids = (m ?? []).map((x: any) => x.user_id);
      if (ids.length) {
        const { data: profs } = await fetchByIds<any>("profiles", "id,full_name,email", ids, "id", supabase).then((d) => ({ data: d, error: null }));
        setOfficers((profs ?? []).map((p: any) => ({ id: p.id, name: p.full_name ?? p.email ?? "Unknown" })));
      }
    })();
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const fromIso = new Date(from + "T00:00:00").toISOString();
      const toIso = new Date(to + "T23:59:59").toISOString();
      const out: Row[] = [];

      if (type !== "shift") {
        let q = supabase
          .from("incidents")
          .select("id,reference_number,title,severity,category,subtype,site_id,reporter_id,occurrence_at")
          .eq("organization_id", orgId)
          .gte("occurrence_at", fromIso)
          .lte("occurrence_at", toIso)
          .order("occurrence_at", { ascending: false })
          .limit(50);
        if (siteId) q = q.eq("site_id", siteId);
        if (officerId) q = q.eq("reporter_id", officerId);
        const { data } = await q;
        for (const r of (data ?? []) as any[]) {
          out.push({
            kind: "incident",
            id: r.id,
            title: r.title || r.reference_number,
            subtitle: `${r.category}${r.subtype ? " · " + r.subtype : ""}`,
            site_id: r.site_id,
            officer_id: r.reporter_id,
            at: r.occurrence_at,
            link: `/incidents/${r.id}`,
            severity: r.severity,
          });
        }
      }

      if (type !== "incident") {
        let q = supabase
          .from("shift_summary_reports")
          .select("id,shift_id,officer_id,generated_at,status")
          .eq("organization_id", orgId)
          .gte("generated_at", fromIso)
          .lte("generated_at", toIso)
          .order("generated_at", { ascending: false })
          .limit(50);
        if (officerId) q = q.eq("officer_id", officerId);
        const { data } = await q;
        for (const r of (data ?? []) as any[]) {
          out.push({
            kind: "shift",
            id: r.id,
            title: "Shift summary report",
            subtitle: `Status: ${r.status}`,
            site_id: null,
            officer_id: r.officer_id,
            at: r.generated_at,
            link: `/reports/tasks`,
          });
        }
      }

      out.sort((a, b) => (a.at < b.at ? 1 : -1));
      if (!cancelled) {
        setRows(out);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, siteId, officerId, type, from, to]);

  const siteName = (id: string | null) => sites.find((s) => s.id === id)?.name ?? "—";
  const officerName = (id: string | null) => officers.find((o) => o.id === id)?.name ?? "—";

  const reset = () => {
    setSiteId("");
    setOfficerId("");
    setType("all");
    setFrom(sevenDaysAgo);
    setTo(today);
  };

  return (
    <section className="mt-8 rounded-2xl border border-[#C0C0C0]/15 bg-card p-5 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">Overview</h2>
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">Manager</span>
        </div>
        <Button size="sm" variant="ghost" onClick={reset} className="h-7 px-2 text-xs">
          <Filter className="mr-1 h-3 w-3" /> Reset
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Recent incidents and shift summaries across your organization.</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Site</Label>
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Officer</Label>
          <select value={officerId} onChange={(e) => setOfficerId(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
            <option value="">All officers</option>
            {officers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Type</Label>
          <select value={type} onChange={(e) => setType(e.target.value as "all" | "incident" | "shift")} className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
            <option value="all">All</option>
            <option value="incident">Incidents</option>
            <option value="shift">Shift summaries</option>
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">From</Label>
          <Input aria-label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">To</Label>
          <Input aria-label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9" />
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-border">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No reports match these filters.</div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.slice(0, 50).map((r) => (
              <li key={`${r.kind}-${r.id}`} className="flex items-center gap-3 px-4 py-3">
                <span className={`mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full ${r.kind === "incident" ? "bg-primary" : "bg-[#C0C0C0]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{r.title}</span>
                    {r.severity && (
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase ${r.severity === "high" || r.severity === "critical" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {r.severity}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {r.subtitle} · {officerName(r.officer_id)}{r.site_id ? ` · ${siteName(r.site_id)}` : ""} · {new Date(r.at).toLocaleString()}
                  </div>
                </div>
                <a href={r.link} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}